import React from 'react';
import Icon from '../../../components/AppIcon';

const TaskQueue = ({ tasks }) => {
  const getPriorityConfig = (priority) => {
    switch (priority) {
      case 'high':
        return {
          bg: 'bg-red-500/10',
          border: 'border-red-500/20',
          text: 'text-red-500',
          dot: 'bg-red-500'
        };
      case 'medium':
        return {
          bg: 'bg-orange-500/10',
          border: 'border-orange-500/20',
          text: 'text-orange-500',
          dot: 'bg-orange-500'
        };
      default:
        return {
          bg: 'bg-blue-500/10',
          border: 'border-blue-500/20',
          text: 'text-blue-500',
          dot: 'bg-blue-500'
        };
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'completed':
        return { icon: 'CheckCircle', color: '#10b981', label: 'Completed' };
      case 'in-progress':
        return { icon: 'Clock', color: '#f59e0b', label: 'In Progress' };
      case 'overdue':
        return { icon: 'AlertCircle', color: '#ef4444', label: 'Overdue' };
      default:
        return { icon: 'Circle', color: 'var(--color-muted-foreground)', label: 'Pending' };
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Task Queue</h3>
          <p className="text-sm text-muted-foreground">Upcoming compliance activities</p>
        </div>
        <button className="inline-flex items-center gap-2 px-3 py-2 bg-background border border-border rounded-lg hover:bg-muted transition-colors text-sm">
          <Icon name="SlidersHorizontal" size={16} />
          Sort & Filter
        </button>
      </div>

      <div className="space-y-4">
        {tasks?.map((task) => {
          const priorityConfig = getPriorityConfig(task?.priority);
          const statusConfig = getStatusConfig(task?.status);

          return (
            <div
              key={task?.id}
              className={`p-4 rounded-lg border ${priorityConfig?.border} ${
                task?.status === 'overdue' ? 'bg-red-500/5' : 'bg-card'
              } hover:shadow-lg transition-all cursor-pointer`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className={`w-10 h-10 rounded-lg ${priorityConfig?.bg} flex items-center justify-center`}>
                    <Icon name="FileText" size={20} color={priorityConfig?.dot} />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-foreground">{task?.title}</h4>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${priorityConfig?.bg} ${priorityConfig?.text}`}>
                          {task?.priority?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">{task?.description}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Icon name={statusConfig?.icon} size={16} color={statusConfig?.color} />
                      <span className="text-sm font-medium" style={{ color: statusConfig?.color }}>
                        {statusConfig?.label}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <Icon name="Tag" size={14} color="var(--color-muted-foreground)" />
                      <span className="text-xs text-muted-foreground">{task?.regulationType}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="Calendar" size={14} color="var(--color-muted-foreground)" />
                      <span className="text-xs text-muted-foreground">Due: {task?.dueDate}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="User" size={14} color="var(--color-muted-foreground)" />
                      <span className="text-xs text-muted-foreground">{task?.assignedTo}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Icon name="MapPin" size={14} color="var(--color-muted-foreground)" />
                      <span className="text-xs text-muted-foreground">{task?.location}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="FileText" size={14} color="var(--color-muted-foreground)" />
                    <span className="text-xs text-muted-foreground">{task?.regulation}</span>
                  </div>

                  {task?.documents?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {task?.documents?.map((doc, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1.5 px-2 py-1 bg-muted rounded text-xs text-foreground"
                        >
                          <Icon name="Paperclip" size={12} />
                          {doc}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-3 border-t border-border">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="Clock" size={14} />
                      <span>Est. {task?.estimatedHours}h</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background border border-border rounded hover:bg-muted transition-colors text-xs">
                        <Icon name="Edit" size={12} />
                        Edit
                      </button>
                      <button className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors text-xs">
                        <Icon name="CheckCircle" size={12} />
                        Mark Complete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TaskQueue;